<div class="container p-4 bg-whitesmoke">
  <app-breadcrumb [activePanel]="'payments'"></app-breadcrumb>
  
  <!-- Add/Edit Payment Button (single-method flow) -->
  <div class="d-flex justify-content-end mb-3">
    <button mat-raised-button color="primary"
            (click)="openCreateForm()"
            class="d-flex align-items-center">
      <mat-icon class="me-2">{{ paymentMethods.length ? 'edit' : 'payment' }}</mat-icon>
      {{ paymentMethods.length ? 'Edit Payment Details' : 'Add Payment Method' }}
    </button>
  </div>
  

  <!-- Loading State (full-screen centered overlay) -->
  <div *ngIf="loading"
       class="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center"
       style="background: rgba(255,255,255,0.6); z-index: 1050;">
    <mat-spinner diameter="70"></mat-spinner>
    <p class="mt-3 text-muted">Loading payment methods...</p>
  </div>

  <!-- Error State -->
  <div *ngIf="error && !loading" class="alert alert-danger rounded">
    <div class="d-flex align-items-center">
      <mat-icon class="me-2">error</mat-icon>
      <div>
        <h6 class="mb-1">Error Loading Payment Methods</h6>
        <p class="mb-3">{{ error }}</p>
        <button mat-button color="primary" (click)="fetchPaymentMethods()">
          <mat-icon class="me-1">refresh</mat-icon>
          Try Again
        </button>
      </div>
    </div>
  </div>

  <!-- No Payment Methods State -->
  <div *ngIf="!loading && !error && paymentMethods.length === 0" class="text-center py-5">
    <div class="mb-4">
      <mat-icon class="display-1 text-muted">account_balance</mat-icon>
    </div>
    <h4 class="fw-bold">No Payment Methods Found</h4>
    <p class="text-muted mb-4">Add your first payment method to start receiving payments</p>
    <button mat-raised-button color="accent" (click)="openCreateForm()">
      <mat-icon class="me-2">add</mat-icon>
      Add Your First Payment Method
    </button>
  </div>

  <!-- Payment Methods Section -->
  <div *ngIf="!loading && !error && paymentMethods.length > 0">
    <!-- Actions (single-method full width) -->
    <div class="d-flex justify-content-between align-items-center mb-4">
    </div>

    <!-- Single centered payment card (full-width layout for single method) -->
    <div class="row justify-content-center">
      <div class="col-12 col-md-8 col-lg-6 col-xl-5 mb-4"
           *ngFor="let method of paymentMethods; trackBy: trackByMethodId">
        <mat-card class="h-100 shadow-sm border-0 hover-shadow transition-all"
                  style="border-radius: 8px; background: linear-gradient(135deg, #e8f5e8, #fff);">
          <mat-card-header class="px-4 pt-4 pb-2">
            <div class="d-flex justify-content-between align-items-center w-100">
              <div class="me-3">
                <h5 class="mb-1 text-truncate text-primary" *ngIf="method.bankName">{{ method.bankName }}</h5>
                <p class="mb-0 small text-secondary text-truncate" *ngIf="method.accountHolder">{{ method.accountHolder }}</p>
              </div>
              <mat-chip class="bg-success text-white small">Active</mat-chip>
            </div>
          </mat-card-header>
          <mat-card-content class="px-4 py-3">
            <div class="mb-2">
              <div *ngIf="method.bankName || method.accountHolder || method.accountNumber || method.ifsc">
                <p class="small mb-1" *ngIf="method.accountNumber"><strong>Account:</strong> ****{{ getLastFourDigits(method.accountNumber) }}</p>
                <p class="small mb-1" *ngIf="method.ifsc"><strong>IFSC:</strong> {{ method.ifsc }}</p>
                <p class="small mb-1" *ngIf="method.accountHolder"><strong>Account Holder:</strong> {{ method.accountHolder }}</p>
              </div>
              <p class="small mb-0" *ngIf="method.upiId"><strong>UPI:</strong> {{ method.upiId }}</p>
              <p class="small mb-0" *ngIf="method.paymentLink" style="white-space: normal;">
                <strong>Payment Link:</strong>
                <a href="#"
                   (click)="openExternalPaymentLink(method.paymentLink); $event.preventDefault()"
                   target="_blank" rel="noopener"
                   style="display:inline-block; max-width:100%; word-break:break-word; overflow-wrap:anywhere;">
                  {{ method.paymentLink }}
                </a>
              </p>
            </div>
            <div *ngIf="method.qrCodeUrl" class="mt-2 d-flex align-items-center">
              <img [src]="method.qrCodeUrl" alt="QR code" style="max-width:120px; max-height:120px; border-radius:8px; margin-right:12px;" />
              <small class="text-muted">QR Code</small>
            </div>
          </mat-card-content>
          <mat-card-actions
            class="px-4 py-3 bg-light d-flex justify-content-between align-items-center"
            style="border-bottom-left-radius: 8px; border-bottom-right-radius: 8px;">
              <div class="d-flex align-items-center gap-2">
              <button mat-icon-button color="primary" matTooltip="View Details" (click)="viewMethod(method)">
                <mat-icon>visibility</mat-icon>
              </button>
              <button mat-icon-button color="accent"
                      (click)="openCreateForm(method)"
                      matTooltip="Edit Method">
                <mat-icon>edit</mat-icon>
              </button>
              <button mat-icon-button color="warn"
                      (click)="deletePaymentMethod()"
                      matTooltip="Delete Method">
                <mat-icon>delete</mat-icon>
              </button>
            </div>
            <small class="text-muted" *ngIf="method.bankName">{{ method.bankName }}</small>
          </mat-card-actions>
        </mat-card>
      </div>
    </div>

    <!-- List view removed: single-method UI uses full width -->
  </div>
</div>

<!-- Create/Edit Payment Method Dialog -->
<div *ngIf="showCreateForm" class="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-overlay"
     style="background: rgba(0, 0, 0, 0.6); z-index: 9999;">
  <div class="container">
    <div class="row justify-content-center">
      <div class="col-12 col-md-10 col-lg-8">
        <mat-card class="shadow-lg border-0" style="border-radius: 12px; max-height: 90vh; background: #e8f5e8;">
          <mat-card-header class="d-flex justify-content-between align-items-center border-bottom px-4 py-3"
                          style="background: #c8e6c9;">
            <mat-card-title class="fw-bold">
              <mat-icon class="me-2 text-primary">{{ editingMethod ? 'edit' : 'account_balance' }}</mat-icon>
              {{ editingMethod ? 'Edit Payment Method' : 'Add Payment Method' }}
            </mat-card-title>
            <button mat-icon-button (click)="closeCreateForm()">
              <mat-icon>close</mat-icon>
            </button>
          </mat-card-header>

          <!-- Error Display -->
          <div *ngIf="error" class="px-4 pt-3">
            <mat-card class="shadow-sm border-0"
                      style="background-color: #f8d7da; border-left: 4px solid #dc3545;">
              <mat-card-content class="p-3">
                <div class="d-flex align-items-start">
                  <mat-icon class="me-2 text-danger">error</mat-icon>
                  <div class="flex-grow-1">
                    <div class="fw-bold text-danger">An Error Occurred</div>
                    <div class="text-danger">{{ error }}</div>
                  </div>
                  <button type="button" mat-icon-button class="text-danger" (click)="clearError()">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              </mat-card-content>
            </mat-card>
          </div>

          <mat-card-content class="px-4 py-4 overflow-auto" style="max-height: 65vh;">
            <form [formGroup]="paymentForm" (ngSubmit)="onSubmit()">
              <div class="row g-4">
                <div class="col-md-6">
                  <h6 class="text-primary fw-bold d-flex align-items-center mb-3">
                    <mat-icon class="me-2">account_balance</mat-icon> Bank Details
                  </h6>
                  <mat-form-field appearance="outline" class="w-100 mb-3">
                    <mat-label>Bank Name</mat-label>
                    <input matInput formControlName="bankName" placeholder="Enter bank name">
                    <mat-icon matSuffix>account_balance</mat-icon>
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="w-100 mb-3">
                    <mat-label>Account Holder Name</mat-label>
                    <input matInput formControlName="accountHolder" placeholder="Enter account holder name">
                    <mat-icon matSuffix>person</mat-icon>
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="w-100 mb-3">
                    <mat-label>Account Number</mat-label>
                    <input matInput formControlName="accountNumber" placeholder="Enter account number">
                    <mat-icon matSuffix>credit_card</mat-icon>
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="w-100">
                    <mat-label>IFSC Code</mat-label>
                    <input matInput formControlName="ifsc" placeholder="Enter IFSC code">
                    <mat-icon matSuffix>location_on</mat-icon>
                  </mat-form-field>
                </div>
                <div class="col-md-6">
                  <h6 class="text-primary fw-bold d-flex align-items-center mb-3">
                    <mat-icon class="me-2">qr_code</mat-icon> UPI & QR Details
                  </h6>
                  <mat-form-field appearance="outline" class="w-100 mb-3">
                    <mat-label>UPI ID (Optional)</mat-label>
                    <input matInput formControlName="upiId" placeholder="yourname@bank">
                    <mat-icon matSuffix>alternate_email</mat-icon>
                  </mat-form-field>
                  <div class="mb-3">
                    <label class="small fw-bold d-block mb-2">QR Code Image (optional)</label>
                    <input type="file" accept="image/*" (change)="onQrFileSelected($event)" />
                    <div *ngIf="qrPreview" class="mt-3 d-flex align-items-center gap-3">
                      <img [src]="qrPreview" alt="QR preview" style="max-width:120px; max-height:120px; border-radius:8px;" />
                      <div>
                        <button mat-stroked-button color="warn" (click)="removeQrImage()">Remove</button>
                      </div>
                    </div>
                  </div>
                  <mat-form-field appearance="outline" class="w-100 mt-2">
                    <mat-label>Payment Link (Optional)</mat-label>
                    <input matInput formControlName="paymentLink" placeholder="https://payment.example.com/..">
                    <mat-icon matSuffix>link</mat-icon>
                  </mat-form-field>
                    <!-- Link preview: favicon + domain (lightweight, client-side) -->
                    <div *ngIf="paymentForm.get('paymentLink')?.value" class="mt-2 d-flex align-items-center gap-3">
                      <img [src]="getFaviconUrl(paymentForm.get('paymentLink')?.value)" alt="site favicon" style="width:28px;height:28px;border-radius:4px;" />
                      <div class="small text-muted">
                        <div *ngIf="getDomain(paymentForm.get('paymentLink')?.value) as dom">{{ dom }}</div>
                        <a *ngIf="paymentForm.get('paymentLink')?.value" href="#" (click)="openExternalPaymentLink(paymentForm.get('paymentLink')?.value); $event.preventDefault()">Open Link</a>
                      </div>
                    </div>
                </div>
              </div>
            </form>
          </mat-card-content>

          <mat-card-actions class="d-flex justify-content-end gap-3 px-4 py-3 border-top bg-light"
                            style="border-bottom-left-radius: 12px; border-bottom-right-radius: 12px;">
            <button mat-button (click)="closeCreateForm()" class="px-4">Cancel</button>
      <button mat-raised-button color="primary" (click)="onSubmit()"
        [disabled]="creating || !canSave()" class="px-4">
              <div class="d-flex align-items-center">
                <mat-spinner *ngIf="creating" diameter="20" class="me-2"></mat-spinner>
                <mat-icon *ngIf="!creating" class="me-2">{{ editingMethod ? 'save' : 'add' }}</mat-icon>
                <span>{{ creating ? "Processing..." : editingMethod ? "Save Changes" : "Add Method" }}</span>
              </div>
            </button>
          </mat-card-actions>
        </mat-card>
      </div>
    </div>
  </div>
</div>
